import { useCallback, useEffect, useState, type FormEvent } from 'react';

import {
  ApiError,
  assignToWorkPackage,
  createWorkPackage,
  deleteWorkPackage,
  fileIntoWorkPackage,
  listFileable,
  listWorkPackages,
  FILEABLE_KINDS,
  removeFromWorkPackage,
  updateWorkPackage,
  type FileableRecord,
  type WorkPackage,
  type WorkPackageInput,
} from '../api.js';
import { useProjectMembers } from './ProjectMembers.js';

/**
 * Work packages — the subcontract scope somebody is given.
 *
 * WHAT THIS SCREEN ACTUALLY DOES, AND WHY IT IS NOT A REGISTER LIKE THE OTHERS
 * ---------------------------------------------------------------------------
 * Every other register on the project lists records. This one **grants
 * visibility**: `04` scopes eight resources to `assigned` for a subcontractor,
 * and a work package is what "assigned" means. Putting somebody on a package is
 * the act that decides which tasks, snags, inspections and drawings they can
 * see at all.
 *
 * So the people on a package are not a detail behind an Edit form — they are
 * the point, and they are on the row, in words, always. A screen where you have
 * to open something to find out who can read your project's snags is a screen
 * nobody audits.
 *
 * WHY THE COUNTS ARE ON THE ROW
 * -----------------------------
 * An EMPTY package is the failure mode. Somebody lets a subcontract, assigns
 * the trade, files nothing under it — and the trade sees nothing and assumes
 * the app is broken. The counts make that visible without opening anything, and
 * the empty state says it in words rather than leaving a row of zeros to be
 * interpreted.
 *
 * IT LIVES IN RECORDS, BESIDE LOCATIONS
 * -------------------------------------
 * Both are project STRUCTURE that other registers point at, set up once and
 * edited rarely — as opposed to the work itself, which is what the other six
 * sections hold. It is guarded by `project: edit`, which is the same three
 * roles that may edit the project's own details.
 */

type State =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly packages: readonly WorkPackage[] }
  | { readonly status: 'not_permitted' }
  | { readonly status: 'failed'; readonly message: string };

interface Props {
  readonly projectId: string;
  /**
   * A NARROW capability, like every other register's, rather than the whole
   * `ProjectCapabilities` bag. One flag: may this reader let a subcontract.
   * `ProjectDetail` fills it from `edit_project`, which is the same permission
   * the routes declare.
   */
  readonly can: { readonly edit: boolean };
}

export function WorkPackages({ projectId, can }: Props): JSX.Element {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  /** Which package's "add somebody" picker is open. */
  const [assigning, setAssigning] = useState<string | null>(null);
  /** Which package's "file work into it" panel is open. */
  const [filing, setFiling] = useState<string | null>(null);

  // The project's team, from the page's ONE member fetch — the same source the
  // team panel reads, so the two cannot offer different people.
  const members = useProjectMembers(projectId);

  const mayEdit = can.edit;

  const load = useCallback(async () => {
    try {
      setState({ status: 'ready', packages: await listWorkPackages(projectId) });
    } catch (caught) {
      const denied = caught instanceof ApiError && (caught.status === 403 || caught.status === 404);
      setState(
        denied
          ? { status: 'not_permitted' }
          : { status: 'failed', message: 'Could not load the work packages.' },
      );
    }
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<readonly WorkPackage[]>): Promise<boolean> {
    setBusy(true);
    setError(null);
    try {
      setState({ status: 'ready', packages: await work() });
      return true;
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'That did not work.');
      return false;
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="section-head sub-head-row">
      <h3 className="sub-head">Work packages</h3>
      {mayEdit && state.status === 'ready' && !adding && (
        <div className="head-actions">
          <button type="button" className="inline" onClick={() => setAdding(true)}>
            Add work package
          </button>
        </div>
      )}
    </div>
  );

  if (state.status === 'loading') {
    return (
      <>
        {head}
        <p aria-live="polite">Loading work packages…</p>
      </>
    );
  }

  if (state.status === 'not_permitted') {
    return (
      <>
        {head}
        <p className="hint">Your role does not include this project's work packages.</p>
      </>
    );
  }

  if (state.status === 'failed') {
    return (
      <>
        {head}
        <div className="alert" role="alert">
          <span>{state.message}</span>
        </div>
      </>
    );
  }

  return (
    <>
      {head}

      <p className="hint">
        A work package is a scope of work let to a subcontractor — “all joinery”,
        “MEP first fix”. Whoever is on a package sees the tasks, snags,
        inspections and drawings filed under it, and nothing else on the project.
      </p>

      {error && (
        <div className="alert" role="alert">
          <span>{error}</span>
        </div>
      )}

      {adding && (
        <PackageForm
          busy={busy}
          onCancel={() => {
            setAdding(false);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => createWorkPackage(projectId, { ...input, name: input.name ?? '' })))
              setAdding(false);
          }}
        />
      )}

      {state.packages.length === 0 ? (
        <div className="empty">
          <p>
            <strong>No work packages yet.</strong>
          </p>
          <p>
            {mayEdit
              ? 'Everything on this project belongs to the main contractor until a package is added. Add one for each subcontract, then put the trade on it.'
              : // An empty state must name an action ITS reader can take. This
                // reader cannot add one, so it says who can rather than
                // pointing at a button they will not find.
                'Everything on this project belongs to the main contractor. A project manager adds a package for each subcontract.'}
          </p>
        </div>
      ) : (
        <div className="rk-scroll">
          <table className="grid rk-cards">
            <caption className="visually-hidden">
              The work packages on this project, who can see each one, and what is filed under it.
            </caption>
            <thead>
              <tr>
                <th scope="col">Package</th>
                <th scope="col">Subcontractor</th>
                <th scope="col">Who can see it</th>
                <th scope="col">What is filed under it</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {state.packages.map((wp) => (
                <tr key={wp.id}>
                  <th scope="row">
                    {wp.code} {wp.name}
                    {wp.description && <span className="hint"> {wp.description}</span>}
                  </th>
                  <td data-label="Subcontractor">
                    {wp.contractor_name ?? <span className="hint">Not named</span>}
                  </td>
                  <td data-label="Who can see it">
                    {wp.members.length === 0 ? (
                      // Not a blank cell. A package with nobody on it grants
                      // nothing, and that is worth saying rather than leaving
                      // somebody to work out from an empty space.
                      <span className="hint">Nobody yet</span>
                    ) : (
                      <ul className="attachment-list">
                        {wp.members.map((m) => (
                          <li key={m.user_id}>
                            {m.name ?? 'Somebody'}
                            {mayEdit && (
                              <button
                                type="button"
                                className="link-button"
                                disabled={busy}
                                onClick={() =>
                                  void run(() =>
                                    removeFromWorkPackage(projectId, wp.id, m.user_id),
                                  )
                                }
                              >
                                Take {m.name ?? 'them'} off {wp.code}
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                    {mayEdit &&
                      (assigning === wp.id ? (
                        <AssignPicker
                          busy={busy}
                          code={wp.code}
                          candidates={members.filter(
                            (m) => !wp.members.some((on) => on.user_id === m.user_id),
                          )}
                          onCancel={() => setAssigning(null)}
                          onPick={async (userId) => {
                            if (await run(() => assignToWorkPackage(projectId, wp.id, userId)))
                              setAssigning(null);
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          className="link-button"
                          disabled={busy}
                          onClick={() => setAssigning(wp.id)}
                        >
                          Put somebody on {wp.code}
                        </button>
                      ))}
                  </td>
                  <td data-label="What is filed under it">
                    <Counts counts={wp.record_counts} />
                    {mayEdit &&
                      (filing === wp.id ? (
                        <FilingPanel
                          projectId={projectId}
                          busy={busy}
                          code={wp.code}
                          packageId={wp.id}
                          onClose={() => setFiling(null)}
                          onFiled={(packages) => setState({ status: 'ready', packages })}
                          onError={setError}
                        />
                      ) : (
                        <button
                          type="button"
                          className="link-button"
                          disabled={busy}
                          onClick={() => setFiling(wp.id)}
                        >
                          File work into {wp.code}
                        </button>
                      ))}
                  </td>
                  <td className="row-actions">
                    {mayEdit && (
                      <>
                        <button
                          type="button"
                          className="link-button"
                          disabled={busy}
                          onClick={() => setEditing(wp.id)}
                        >
                          Edit {wp.code}
                        </button>
                        <button
                          type="button"
                          className="link-button"
                          disabled={busy}
                          onClick={() => void run(() => deleteWorkPackage(projectId, wp.id))}
                        >
                          Remove {wp.code}
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PackageForm
          busy={busy}
          existing={state.packages.find((p) => p.id === editing)}
          onCancel={() => {
            setEditing(null);
            setError(null);
          }}
          onSubmit={async (input) => {
            if (await run(() => updateWorkPackage(projectId, editing, input))) setEditing(null);
          }}
        />
      )}
    </>
  );
}

/**
 * What is filed under a package, in words.
 *
 * Only the kinds that have any. A row of seven zeros is noise, and the one
 * thing that matters — that a package is empty — is said as a sentence rather
 * than left to be inferred from a line of nothing.
 */
function Counts({ counts }: { counts: WorkPackage['record_counts'] }): JSX.Element {
  const LABELS: readonly (readonly [keyof WorkPackage['record_counts'], string, string])[] = [
    ['task', 'task', 'tasks'],
    ['snag', 'snag', 'snags'],
    ['ncr', 'NCR', 'NCRs'],
    ['inspection', 'inspection', 'inspections'],
    ['activity', 'activity', 'activities'],
    ['submittal', 'submittal', 'submittals'],
    ['deliverable', 'drawing', 'drawings'],
  ];

  const parts = LABELS.filter(([key]) => counts[key] > 0).map(
    ([key, one, many]) => `${counts[key]} ${counts[key] === 1 ? one : many}`,
  );

  if (parts.length === 0) {
    return (
      <span className="hint">
        Nothing yet — anybody on this package sees an empty project until work is filed under it
      </span>
    );
  }
  return <>{parts.join(' · ')}</>;
}

/**
 * Choose which of the project's records belong to this subcontract.
 *
 * ONE PLACE, ALL SEVEN KINDS, rather than a "work package" field on seven
 * record forms. Filing work into a subcontract is one job done once by the
 * person who just let it — not seven decisions scattered across seven
 * registers that somebody has to remember while raising a snag.
 *
 * Records already in ANOTHER package are shown and marked, never hidden: a
 * record can only be in one package, so "why is that snag not in my list" has
 * to be answerable without opening every other package in turn.
 */
function FilingPanel({
  projectId,
  busy,
  code,
  packageId,
  onClose,
  onFiled,
  onError,
}: {
  readonly projectId: string;
  readonly busy: boolean;
  readonly code: string;
  readonly packageId: string;
  readonly onClose: () => void;
  readonly onFiled: (packages: readonly WorkPackage[]) => void;
  readonly onError: (message: string) => void;
}): JSX.Element {
  const [kind, setKind] = useState<string>(FILEABLE_KINDS[0].type);
  const [records, setRecords] = useState<readonly FileableRecord[] | null>(null);

  const load = useCallback(async () => {
    setRecords(null);
    try {
      setRecords(await listFileable(projectId, kind));
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'Could not load that list.');
      setRecords([]);
    }
  }, [projectId, kind, onError]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(record: FileableRecord): Promise<void> {
    const mine = record.work_package_id === packageId;
    try {
      onFiled(await fileIntoWorkPackage(projectId, kind, record.id, mine ? null : packageId));
      await load();
    } catch (caught) {
      onError(caught instanceof ApiError ? caught.message : 'That did not work.');
    }
  }

  const label = FILEABLE_KINDS.find((k) => k.type === kind)?.label ?? kind;

  return (
    <div className="panel">
      <p className="panel-title">File work into {code}</p>

      <div className="field">
        <label htmlFor={`filing-kind-${code}`}>What kind of work</label>
        <select
          id={`filing-kind-${code}`}
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          disabled={busy}
        >
          {FILEABLE_KINDS.map((k) => (
            <option key={k.type} value={k.type}>
              {k.label}
            </option>
          ))}
        </select>
      </div>

      {records === null ? (
        <p aria-live="polite">Loading {label.toLowerCase()}…</p>
      ) : records.length === 0 ? (
        <p className="hint">
          There are no {label.toLowerCase()} on this project yet. Raise them in their own register
          first, then file them here.
        </p>
      ) : (
        <ul className="attachment-list">
          {records.map((record) => {
            const mine = record.work_package_id === packageId;
            const elsewhere = record.work_package_id !== null && !mine;
            return (
              <li key={record.id}>
                {record.label}
                {mine && <span className="hint"> · in {code}</span>}
                {/* Shown and marked, never hidden — see the note above. */}
                {elsewhere && <span className="hint"> · already in another package</span>}
                {!elsewhere && (
                  <button
                    type="button"
                    className="link-button"
                    disabled={busy}
                    onClick={() => void toggle(record)}
                  >
                    {mine ? `Take ${record.label} out of ${code}` : `Put ${record.label} in ${code}`}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="button-row">
        <button type="button" className="secondary inline" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

/** Add somebody to a package, from the project's own team. */
function AssignPicker({
  busy,
  code,
  candidates,
  onCancel,
  onPick,
}: {
  readonly busy: boolean;
  readonly code: string;
  readonly candidates: readonly { readonly user_id: string; readonly name: string }[];
  readonly onCancel: () => void;
  readonly onPick: (userId: string) => void;
}): JSX.Element {
  const [chosen, setChosen] = useState('');
  const id = `assign-${code}`;

  if (candidates.length === 0) {
    // The empty state names the action, and it is somebody ELSE's screen: a
    // work package decides what a person sees, never whether they are on the
    // job, so the fix is on the team panel.
    return (
      <p className="hint">
        Everybody on this project is already on {code}. Add them to the team first if somebody is
        missing.
      </p>
    );
  }

  return (
    <div className="field">
      <label htmlFor={id}>Put somebody on {code}</label>
      <select id={id} value={chosen} onChange={(e) => setChosen(e.target.value)} disabled={busy}>
        <option value="">Choose a person…</option>
        {candidates.map((m) => (
          <option key={m.user_id} value={m.user_id}>
            {m.name}
          </option>
        ))}
      </select>
      <div className="button-row">
        <button
          type="button"
          className="inline"
          disabled={busy || !chosen}
          onClick={() => onPick(chosen)}
        >
          Add them
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function PackageForm({
  busy,
  existing,
  onCancel,
  onSubmit,
}: {
  readonly busy: boolean;
  readonly existing?: WorkPackage | undefined;
  readonly onCancel: () => void;
  readonly onSubmit: (input: WorkPackageInput) => void;
}): JSX.Element {
  const [name, setName] = useState(existing?.name ?? '');
  const [contractor, setContractor] = useState(existing?.contractor_name ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');

  function submit(event: FormEvent): void {
    event.preventDefault();
    onSubmit({
      name: name.trim(),
      contractor_name: contractor.trim() || null,
      description: description.trim() || null,
    });
  }

  return (
    <form className="panel" onSubmit={submit}>
      <p className="panel-title">
        {existing ? `Edit ${existing.code}` : 'Add a work package'}
      </p>

      <div className="field">
        <label htmlFor="wp-name">What the scope is</label>
        <input
          id="wp-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={200}
          disabled={busy}
          placeholder="All joinery"
          required
        />
      </div>

      <div className="field">
        <label htmlFor="wp-contractor">Who is doing it (optional)</label>
        <input
          id="wp-contractor"
          value={contractor}
          onChange={(e) => setContractor(e.target.value)}
          maxLength={200}
          disabled={busy}
          placeholder="Gulf Interiors LLC"
        />
        {/* In words, not a login. The subcontractor company may never have an
            account here, and what gets read is the name on the subcontract. */}
        <p className="hint">
          The company on the subcontract. Giving their people access is separate — put them on the
          package once it exists.
        </p>
      </div>

      <div className="field">
        <label htmlFor="wp-description">What it covers (optional)</label>
        <textarea
          id="wp-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={4000}
          disabled={busy}
        />
      </div>

      <div className="button-row">
        <button type="submit" className="inline" disabled={busy || !name.trim()}>
          {existing ? 'Save' : 'Add it'}
        </button>
        <button type="button" className="secondary inline" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
